import { Download, Plus, SearchX, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { EtiquetaDaOpcao } from '@/componentes/EtiquetaDaOpcao';
import { Seletor } from '@/componentes/Seletor';
import { formatarInteiro, interpretarNumero } from '@/lib/formato';
import { conteudoDoQr } from '@/lib/qr';
import { anunciar } from '@/lib/retorno';
import { combinaComTermo } from '@/lib/texto';

import { useFrota } from './consultas-frota';
import type { EquipamentoDaFrota } from './frota';
import {
  FOLHAS_PADRAO,
  MEDIDA_PADRAO,
  desenharConjunto,
  erroDaFolha,
  erroDaMedida,
  formatarMm,
  imporNaFolha,
  menorFolhaQueCabe,
  resumoDoArquivo,
  type Folha,
  type MedidaDoConjunto,
  type MedirTexto,
} from './identificacao/folha';
import { PreviaDoConjunto } from './identificacao/PreviaDoConjunto';
import { codigosDoPrefixoNovo, ocupadosDe, proximosDaSerie, resumoDoLote, seriesDaFrota, type Lote } from './identificacao/series';
import { etiquetasDaMaquina } from './opcao-da-maquina';

/**
 * IDENTIFICAÇÃO DAS MÁQUINAS (pedido do dono, 31/10/2026; decisão 64).
 *
 * *"Criar dentro de equipamentos um novo módulo chamado Identificação. Ele
 * vai servir para gerar o arquivo que vamos usar para fazer os adesivos de
 * identificação dos equipamentos cadastrados no sistema. [...] Esse arquivo a
 * gente vai mandar para a gráfica. Importante: os QR também são o código."*
 *
 * A tela escolhe O QUE vai no arquivo (as máquinas cadastradas e os códigos
 * das que ainda vão chegar), a MEDIDA e a FOLHA, mostra um conjunto na
 * proporção de verdade e baixa o PDF. O laranja da tela é um só: baixar.
 *
 * Os códigos novos nunca repetem um que existe (`identificacao/series.ts`): a
 * frota TEM de ter carregado antes de eles serem gerados. Sem a frota, a
 * seção diz por que está parada, em vez de gerar às cegas.
 *
 * Substitui os "Adesivos de leitura" de 26/10/2026 (`/impressao/adesivos`
 * leva para cá).
 */

type IdDaFolha = Folha['id'];
type ModoNovo = 'serie' | 'prefixo';

interface LoteIncluido {
  readonly id: number;
  readonly codigos: readonly string[];
}

const OPCOES_DE_FOLHA: readonly { readonly valor: IdDaFolha; readonly rotulo: string }[] = [
  ...FOLHAS_PADRAO.map((f) => ({ valor: f.id, rotulo: f.nome })),
  { valor: 'personalizada', rotulo: 'Personalizada' },
];

const OPCOES_DE_EXTRAS = [
  { valor: '0', rotulo: 'Nenhum' },
  { valor: '1', rotulo: '1' },
  { valor: '2', rotulo: '2' },
  { valor: '3', rotulo: '3' },
] as const;

/** Número digitado: `null` quando não é número. */
const numero = (texto: string): number | null => interpretarNumero(texto);

export function PaginaIdentificacao() {
  const frota = useFrota();
  const maquinas = useMemo(() => frota.data ?? [], [frota.data]);

  const [termo, setTermo] = useState('');
  const [escolhidas, setEscolhidas] = useState<ReadonlySet<string>>(new Set());
  const [lotes, setLotes] = useState<readonly LoteIncluido[]>([]);

  const [largura, setLargura] = useState(String(MEDIDA_PADRAO.largura));
  const [altura, setAltura] = useState(String(MEDIDA_PADRAO.altura));
  const [extras, setExtras] = useState(String(MEDIDA_PADRAO.extras));
  /* `null` é "a menor folha padrão em que cabe": a escolha da tela até a
     pessoa escolher outra. */
  const [folhaEscolhida, setFolhaEscolhida] = useState<IdDaFolha | null>(null);
  const [folhaLargura, setFolhaLargura] = useState('330');
  const [folhaAltura, setFolhaAltura] = useState('480');
  const [gerando, setGerando] = useState(false);

  const medir = useMedidorDoCodigo();

  const medida: MedidaDoConjunto = {
    largura: numero(largura) ?? Number.NaN,
    altura: numero(altura) ?? Number.NaN,
    extras: Number(extras),
  };
  const erroMedida = erroDaMedida(medida);
  const folhaAutomatica = erroMedida === null ? menorFolhaQueCabe(medida) : null;
  const idDaFolha: IdDaFolha = folhaEscolhida ?? folhaAutomatica?.id ?? 'a3';
  const folha: Folha =
    idDaFolha === 'personalizada'
      ? { id: 'personalizada', nome: 'folha personalizada', largura: numero(folhaLargura) ?? Number.NaN, altura: numero(folhaAltura) ?? Number.NaN }
      : (FOLHAS_PADRAO.find((f) => f.id === idDaFolha) ?? FOLHAS_PADRAO[1] ?? { id: 'a3', nome: 'A3', largura: 297, altura: 420 });
  const erroFolha = erroMedida === null ? erroDaFolha(medida, folha) : null;
  const imposicao = erroMedida === null && erroFolha === null ? imporNaFolha(medida, folha) : null;

  /* O que vai no arquivo: as cadastradas na ordem da frota, e os lotes novos na ordem em que entraram. */
  const doCadastro = useMemo(
    () => maquinas.filter((m) => escolhidas.has(m.id)).map((m) => conteudoDoQr(m.codigo)).filter((c): c is string => c !== null),
    [maquinas, escolhidas],
  );
  const novos = useMemo(() => lotes.flatMap((l) => l.codigos), [lotes]);
  const codigos = [...doCadastro, ...novos];

  const ocupados = useMemo(() => ocupadosDe([...maquinas.map((m) => m.codigo), ...novos]), [maquinas, novos]);
  const filtradas = useMemo(
    () => maquinas.filter((m) => combinaComTermo([m.codigo, m.codigoInterno, m.placa, m.tipo, m.modelo, m.descricao], termo)),
    [maquinas, termo],
  );

  const podeBaixar = codigos.length > 0 && imposicao !== null && !gerando;

  async function baixar() {
    if (imposicao === null) return;
    setGerando(true);
    try {
      const { gerarPdfDaIdentificacao, nomeDoArquivo } = await import('./identificacao/pdf');
      const quando = new Date();
      const bytes = await gerarPdfDaIdentificacao({ codigos, medida, folha, quando });
      const nome = nomeDoArquivo(quando);
      const url = URL.createObjectURL(new Blob([bytes as Uint8Array<ArrayBuffer>], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = nome;
      link.click();
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 10_000);
      const folhas = Math.ceil(codigos.length / imposicao.porFolha);
      anunciar({
        tom: 'sucesso',
        titulo: 'Arquivo de identificação baixado',
        alvo: { nome },
        fatos: [
          `${formatarInteiro(codigos.length)} ${codigos.length === 1 ? 'conjunto' : 'conjuntos'}`,
          `${formatarInteiro(folhas)} ${folhas === 1 ? 'folha' : 'folhas'} de ${folha.nome}`,
          `${formatarMm(medida.largura)} × ${formatarMm(medida.altura)} mm`,
        ],
        efeito: 'Mande o arquivo à gráfica: o corte é nas marcas de cada peça.',
      });
    } catch (erro) {
      anunciar({
        tom: 'falha',
        titulo: 'Arquivo de identificação não gerado',
        comoResolver: erro instanceof Error ? `${erro.message} Ajuste e baixe de novo.` : 'Tente de novo; se continuar, avise o suporte com o horário.',
      });
    } finally {
      setGerando(false);
    }
  }

  const alternar = (id: string) => {
    setEscolhidas((agora) => {
      const nova = new Set(agora);
      if (nova.has(id)) nova.delete(id);
      else nova.add(id);
      return nova;
    });
  };
  const todasFiltradas = filtradas.length > 0 && filtradas.every((m) => escolhidas.has(m.id));

  return (
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina">
        <CabecalhoPagina
          sobretitulo="Frota"
          titulo="Identificação"
          subtitulo="O arquivo dos adesivos de identificação das máquinas, com o código e os QR, para mandar à gráfica"
          acoes={
            <Botao nivel="primario" disabled={!podeBaixar} carregando={gerando} onClick={() => void baixar()}>
              <Download aria-hidden="true" className="size-[var(--icone-controle)]" />
              Baixar arquivo
            </Botao>
          }
        />

        <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div className="flex min-w-0 flex-col gap-6">
            {/* AS CADASTRADAS */}
            <section aria-labelledby="identificacao-cadastradas" className="cartao flex flex-col gap-4 p-6">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 id="identificacao-cadastradas" className="txt-secao text-tinta">
                  Máquinas cadastradas
                </h2>
                {frota.data ? (
                  <span className="txt-apoio tabular-nums text-tinta-secundaria">
                    {formatarInteiro(escolhidas.size)} de {formatarInteiro(maquinas.length)} no arquivo
                  </span>
                ) : null}
              </div>
              {frota.data && maquinas.length > 0 ? (
                <div className="flex flex-wrap items-end gap-3">
                  <div className="min-w-60 flex-1">
                    <CampoTexto
                      rotulo="Procurar máquina"
                      opcional
                      valor={termo}
                      aoMudar={setTermo}
                      aplicado={termo.trim() !== ''}
                      placeholder="Código, código interno, placa ou tipo"
                    />
                  </div>
                  <Botao
                    nivel="secundario"
                    disabled={filtradas.length === 0}
                    onClick={() => {
                      setEscolhidas((agora) => {
                        const nova = new Set(agora);
                        for (const m of filtradas) {
                          if (todasFiltradas) nova.delete(m.id);
                          else if (conteudoDoQr(m.codigo) !== null) nova.add(m.id);
                        }
                        return nova;
                      });
                    }}
                  >
                    {todasFiltradas ? 'Tirar todas' : 'Escolher todas'}
                  </Botao>
                </div>
              ) : null}
              <ListaDasCadastradas
                carregando={frota.isPending}
                erro={frota.isError ? frota.error : null}
                aoTentarDeNovo={() => void frota.refetch()}
                total={maquinas.length}
                maquinas={filtradas}
                escolhidas={escolhidas}
                aoAlternar={alternar}
                comTermo={termo.trim() !== ''}
                aoLimpar={() => {
                  setTermo('');
                }}
              />
            </section>

            {/* AS QUE AINDA VÃO CHEGAR */}
            <section aria-labelledby="identificacao-novos" className="cartao flex flex-col gap-4 p-6">
              <div className="flex flex-col gap-1">
                <h2 id="identificacao-novos" className="txt-secao text-tinta">
                  Códigos ainda sem máquina
                </h2>
                <p className="txt-apoio text-tinta-secundaria">
                  Para o adesivo ficar pronto antes de a máquina chegar. O código é o que o sistema vai criar no cadastro: a
                  série continua de onde a frota parou.
                </p>
              </div>
              {frota.data ? (
                <CodigosNovos
                  maquinas={maquinas}
                  ocupados={ocupados}
                  aoIncluir={(novo) => {
                    setLotes((agora) => [...agora, { id: Date.now(), codigos: novo }]);
                  }}
                />
              ) : frota.isPending ? (
                <Carregando oQue="Carregando as séries da frota" className="flex flex-col gap-3">
                  <Barra className="h-[var(--controle-m)] w-full rounded-controle" />
                  <Barra className="h-[var(--controle-m)] w-2/3 rounded-controle" />
                </Carregando>
              ) : (
                <p className="txt-apoio text-tinta-secundaria">
                  Sem a frota carregada não dá para conferir se o código já existe. Carregue a lista das máquinas de novo e
                  gere os códigos depois.
                </p>
              )}
              {lotes.length > 0 ? (
                <ul aria-label="Códigos novos no arquivo" className="flex flex-wrap gap-2">
                  {lotes.map((l) => (
                    <li key={l.id} className="chip gap-2">
                      <span className="mono">{resumoDoLote(l.codigos)}</span>
                      <button
                        type="button"
                        aria-label={`Retirar ${resumoDoLote(l.codigos)} do arquivo`}
                        title="Retirar do arquivo"
                        className="inline-flex size-6 items-center justify-center rounded-pill text-tinta-secundaria hover:bg-rebaixada"
                        onClick={() => {
                          setLotes((agora) => agora.filter((x) => x.id !== l.id));
                        }}
                      >
                        <X aria-hidden="true" className="size-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          </div>

          {/* A MEDIDA, A FOLHA E A PRÉVIA */}
          <section aria-labelledby="identificacao-medida" className="cartao flex min-w-0 flex-col gap-4 p-6">
            <h2 id="identificacao-medida" className="txt-secao text-tinta">
              Medida e folha
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <CampoTexto rotulo="Largura do conjunto (mm)" valor={largura} aoMudar={setLargura} placeholder="300" />
              <CampoTexto rotulo="Altura do conjunto (mm)" valor={altura} aoMudar={setAltura} placeholder="100" />
            </div>
            <div className="flex flex-col gap-2">
              <span className="txt-rotulo-campo text-tinta">QR à parte, por máquina</span>
              <Seletor rotulo="QR à parte, por máquina" valor={extras} opcoes={OPCOES_DE_EXTRAS} aoEscolher={setExtras} />
            </div>
            <div className="flex flex-col gap-2">
              <span className="txt-rotulo-campo text-tinta">Folha</span>
              <Seletor
                rotulo="Folha"
                valor={idDaFolha}
                opcoes={OPCOES_DE_FOLHA}
                aoEscolher={(id) => {
                  setFolhaEscolhida(id);
                }}
              />
            </div>
            {idDaFolha === 'personalizada' ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <CampoTexto rotulo="Largura da folha (mm)" valor={folhaLargura} aoMudar={setFolhaLargura} placeholder="330" />
                <CampoTexto rotulo="Altura da folha (mm)" valor={folhaAltura} aoMudar={setFolhaAltura} placeholder="480" />
              </div>
            ) : null}

            {erroMedida !== null ? (
              <p role="alert" className="bloco-rebaixado txt-apoio p-3 text-tinta">
                {erroMedida}
              </p>
            ) : erroFolha !== null ? (
              <div role="alert" className="bloco-rebaixado flex flex-col items-start gap-2 p-3">
                <p className="txt-apoio text-tinta">{erroFolha}</p>
                {menorFolhaQueCabe(medida) ? (
                  <Botao
                    nivel="secundario"
                    tamanho="p"
                    onClick={() => {
                      setFolhaEscolhida(menorFolhaQueCabe(medida)?.id ?? null);
                    }}
                  >
                    {`Usar ${menorFolhaQueCabe(medida)?.nome ?? ''}`}
                  </Botao>
                ) : null}
              </div>
            ) : null}

            {erroMedida === null ? (
              <figure className="flex flex-col gap-2">
                <div className="rounded-controle bg-rebaixada p-4">
                  {medir === null ? (
                    <Carregando oQue="Carregando a prévia do adesivo">
                      <Barra className="aspect-[3/1] w-full rounded-etiqueta" />
                    </Carregando>
                  ) : (
                    <PreviaDoConjunto
                      codigo={codigos[0] ?? 'BAS-001'}
                      medida={medida}
                      desenho={desenharConjunto(medida)}
                      medir={medir.medir}
                      maiusculaPorCorpo={medir.maiuscula}
                    />
                  )}
                </div>
                <figcaption className="txt-apoio text-tinta-secundaria">
                  {codigos[0] ? `Um conjunto, ${codigos[0]}` : 'Um conjunto de exemplo'}, na proporção de verdade:{' '}
                  {formatarMm(medida.largura)} × {formatarMm(medida.altura)} mm. Cada QR leva o código, e cada peça tem
                  marca de corte.
                </figcaption>
              </figure>
            ) : null}

            {imposicao !== null ? (
              <p className="txt-corpo tabular-nums font-semibold text-tinta">
                {resumoDoArquivo(codigos.length, imposicao.porFolha, folha)}
              </p>
            ) : null}
            {imposicao !== null && codigos.length === 0 ? (
              <p className="txt-apoio text-tinta-secundaria">Escolha as máquinas ou gere códigos novos para baixar o arquivo.</p>
            ) : null}
          </section>
        </div>
      </div>
    </div>
  );
}

/** A métrica da fonte do PDF, carregada com o gerador, que é um pedaço à parte do pacote. */
function useMedidorDoCodigo(): { readonly medir: MedirTexto; readonly maiuscula: number } | null {
  const [medidor, setMedidor] = useState<{ readonly medir: MedirTexto; readonly maiuscula: number } | null>(null);
  useEffect(() => {
    let vivo = true;
    void import('./identificacao/pdf').then(async (m) => {
      const medir = await m.medidorDoCodigo();
      if (vivo) setMedidor({ medir, maiuscula: m.MAIUSCULA_DA_HELVETICA_NEGRITO });
    });
    return () => {
      vivo = false;
    };
  }, []);
  return medidor;
}

/** Os cinco estados da lista (p.34): carregando, erro (e sem permissão), vazia, sem resultado e a lista. */
function ListaDasCadastradas({
  carregando,
  erro,
  aoTentarDeNovo,
  total,
  maquinas,
  escolhidas,
  aoAlternar,
  comTermo,
  aoLimpar,
}: {
  readonly carregando: boolean;
  readonly erro: unknown;
  readonly aoTentarDeNovo: () => void;
  readonly total: number;
  readonly maquinas: readonly EquipamentoDaFrota[];
  readonly escolhidas: ReadonlySet<string>;
  readonly aoAlternar: (id: string) => void;
  readonly comTermo: boolean;
  readonly aoLimpar: () => void;
}) {
  if (carregando) {
    return (
      <Carregando oQue="Carregando a frota" className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-2">
        {Array.from({ length: 8 }, (_, i) => (
          <Barra key={i} className="h-12 w-full rounded-controle" />
        ))}
      </Carregando>
    );
  }
  if (erro) return <ErroDaLista oQue="As máquinas" erro={erro} aoTentarDeNovo={aoTentarDeNovo} />;
  if (total === 0) {
    return (
      <EstadoVazio
        tom="marca"
        titulo="Nenhuma máquina cadastrada"
        descricao="Gere os códigos das máquinas que vão chegar em Códigos ainda sem máquina, logo abaixo, ou cadastre a primeira máquina."
      />
    );
  }
  if (maquinas.length === 0 && comTermo) {
    return (
      <EstadoVazio
        icone={SearchX}
        titulo="Nenhuma máquina com esse termo"
        descricao="Confira o código, o código interno ou a placa, ou limpe a busca."
        acao={
          <Botao nivel="secundario" onClick={aoLimpar}>
            Limpar a busca
          </Botao>
        }
      />
    );
  }
  return (
    <ul className="grid max-h-[480px] grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-2 overflow-y-auto">
      {maquinas.map((m) => {
        const codigo = conteudoDoQr(m.codigo);
        const ligada = escolhidas.has(m.id);
        return (
          <li key={m.id}>
            <label
              className={`flex min-h-12 cursor-pointer items-center gap-3 rounded-controle border px-3 py-2 transition-colors ${
                ligada ? 'border-marca bg-marca-fundo' : 'border-borda bg-card hover:bg-rebaixada'
              } ${codigo === null ? 'cursor-not-allowed opacity-60' : ''}`}
              title={codigo === null ? 'O código desta máquina tem espaço ou sinal que o QR não leva. Corrija o cadastro.' : undefined}
            >
              <input
                type="checkbox"
                checked={ligada}
                disabled={codigo === null}
                onChange={() => {
                  aoAlternar(m.id);
                }}
              />
              <span className="flex min-w-0 flex-col">
                <span className="flex flex-wrap items-center gap-1">
                  <span className="mono txt-corpo font-semibold text-tinta">{m.codigo}</span>
                  {etiquetasDaMaquina(m).map((e) => (
                    <EtiquetaDaOpcao key={e.texto} etiqueta={e} />
                  ))}
                </span>
                {m.tipo ?? m.descricao ? <span className="txt-linha2 truncate text-tinta-secundaria">{m.tipo ?? m.descricao}</span> : null}
              </span>
            </label>
          </li>
        );
      })}
    </ul>
  );
}

/** Os códigos novos: continuar uma série da frota, ou um prefixo novo. */
function CodigosNovos({
  maquinas,
  ocupados,
  aoIncluir,
}: {
  readonly maquinas: readonly EquipamentoDaFrota[];
  readonly ocupados: ReturnType<typeof ocupadosDe>;
  readonly aoIncluir: (codigos: readonly string[]) => void;
}) {
  const series = useMemo(() => seriesDaFrota(maquinas.map((m) => m.codigo)), [maquinas]);
  const [modo, setModo] = useState<ModoNovo>(series.length > 0 ? 'serie' : 'prefixo');
  const [chave, setChave] = useState(series[0]?.chave ?? '');
  const [quantos, setQuantos] = useState('3');
  const [prefixo, setPrefixo] = useState('');
  const [inicio, setInicio] = useState('1');
  const [quantidade, setQuantidade] = useState('3');
  const [algarismos, setAlgarismos] = useState('3');

  const serie = series.find((s) => s.chave === chave) ?? series[0];
  const lote: Lote | null =
    modo === 'serie'
      ? serie
        ? proximosDaSerie(serie, numero(quantos) ?? 0, ocupados)
        : null
      : prefixo.trim() === ''
        ? null
        : codigosDoPrefixoNovo(
            { prefixo, inicio: numero(inicio) ?? 0, quantidade: numero(quantidade) ?? 0, algarismos: numero(algarismos) ?? 0 },
            ocupados,
          );

  return (
    <div className="flex flex-col gap-4">
      <Seletor
        rotulo="Como gerar"
        valor={modo}
        opcoes={[
          { valor: 'serie', rotulo: 'Continuar uma série' },
          { valor: 'prefixo', rotulo: 'Prefixo novo' },
        ]}
        aoEscolher={setModo}
      />
      {modo === 'serie' ? (
        series.length === 0 ? (
          <p className="txt-apoio text-tinta-secundaria">
            A frota ainda não tem código em série (letras, traço e número). Use um prefixo novo.
          </p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <CampoSelecao
              rotulo="Série"
              valor={serie?.chave ?? ''}
              aoMudar={setChave}
              opcoes={series.map((s) => ({
                valor: s.chave,
                rotulo: s.prefixo,
                detalhe: `Até ${s.ultimo} · ${formatarInteiro(s.quantidade)} ${s.quantidade === 1 ? 'máquina' : 'máquinas'}`,
              }))}
            />
            <CampoTexto rotulo="Quantas a mais" valor={quantos} aoMudar={setQuantos} placeholder="3" />
          </div>
        )
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <CampoTexto rotulo="Prefixo" valor={prefixo} aoMudar={setPrefixo} placeholder="PA" maxLength={5} ajuda="De 2 a 5 letras." />
          <CampoTexto rotulo="Começa em" valor={inicio} aoMudar={setInicio} placeholder="1" />
          <CampoTexto rotulo="Quantidade" valor={quantidade} aoMudar={setQuantidade} placeholder="3" />
          <CampoTexto rotulo="Algarismos" valor={algarismos} aoMudar={setAlgarismos} placeholder="3" ajuda="O sistema usa 3: ESC-007." />
        </div>
      )}

      {lote === null ? null : 'erro' in lote ? (
        <p role="alert" className="bloco-rebaixado txt-apoio p-3 text-tinta">
          {lote.erro}
        </p>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="txt-apoio text-tinta-secundaria">
            {lote.codigos.length === 1 ? 'Entra' : 'Entram'} <span className="mono text-tinta">{resumoDoLote(lote.codigos)}</span>
          </p>
          <Botao
            nivel="secundario"
            onClick={() => {
              aoIncluir(lote.codigos);
            }}
          >
            <Plus aria-hidden="true" className="size-[var(--icone-controle)]" />
            Incluir no arquivo
          </Botao>
        </div>
      )}
    </div>
  );
}
